import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Calculator, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { ItemListEditor } from './ItemListEditor';
import { EstimatesManager } from './EstimatesManager';
import { PRICING_SCHEMAS, FOOD_AVAILABILITY_OPTIONS } from './schemas';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULES = [
  { key: 'venue_packages', label: 'Venue Packages (Model A)', hint: 'Entire venue sold as one package' },
  { key: 'per_person_packages', label: 'Per-Person Packages (Model D)', hint: 'Charged by guest count' },
  { key: 'event_spaces', label: 'Event Spaces (Model B)', hint: 'Spaces priced individually' },
  { key: 'rooms', label: 'Rooms (Model C)', hint: 'Room-based pricing & inventory' },
  { key: 'food', label: 'Food Packages', hint: 'In-house food module' },
  { key: 'addons', label: 'Add-Ons (Model E)', hint: 'Custom chargeable components' }
];

const SUB_TABS = [
  { key: 'model', label: 'Selling Model' },
  { key: 'durations', label: 'Durations' },
  { key: 'venue_packages', label: 'Venue Packages' },
  { key: 'per_person_packages', label: 'Per-Person' },
  { key: 'event_spaces', label: 'Event Spaces' },
  { key: 'room_categories', label: 'Rooms' },
  { key: 'food', label: 'Food' },
  { key: 'addons', label: 'Add-Ons' },
  { key: 'taxes_charges', label: 'Taxes & Charges' },
  { key: 'discounts', label: 'Discounts' },
  { key: 'seasonal', label: 'Seasonal Pricing' },
  { key: 'rules', label: 'Rules' },
  { key: 'pdf', label: 'PDF Settings' },
  { key: 'estimates', label: 'Estimates & Leads' }
];

const PDF_FIELDS = [
  ['venue_photos', 'Venue photographs'],
  ['cost_breakdown', 'Detailed cost breakdown'],
  ['taxes', 'Taxes'],
  ['deposits', 'Refundable deposits'],
  ['discounts', 'Discounts'],
  ['payment_schedule', 'Payment schedule'],
  ['cancellation_policy', 'Cancellation policy'],
  ['rules', 'Rules & regulations']
];

const PricingEngineManager = ({ adminToken, venueSlug = null }) => {
  const [venues, setVenues] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(venueSlug || '');
  const [config, setConfig] = useState(null);
  const [venueName, setVenueName] = useState('');
  const [subTab, setSubTab] = useState('model');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (venueSlug) return;
    axios.get(`${API_URL}/admin/venue-pages`, { params: { admin_token: adminToken } })
      .then(res => setVenues(res.data.pages || []))
      .catch(() => toast.error('Failed to load venues'));
  }, [adminToken, venueSlug]);

  const loadConfig = useCallback(async (slug) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/pricing/${slug}`, { params: { admin_token: adminToken } });
      setConfig(res.data.config);
      setVenueName(res.data.venue_name);
      setDirty(false);
    } catch {
      toast.error('Failed to load pricing config');
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (selectedSlug) loadConfig(selectedSlug);
  }, [selectedSlug, loadConfig]);

  const update = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };
  const updateNested = (key, subkey, value) => {
    setConfig(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [subkey]: value } }));
    setDirty(true);
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/admin/pricing/${selectedSlug}`, config, { params: { admin_token: adminToken } });
      toast.success('Pricing configuration saved');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const context = {
    rooms: (config?.room_categories || []).filter(r => !r.archived),
    spaces: (config?.event_spaces || []).filter(s => !s.archived)
  };

  if (!selectedSlug || !config) {
    if (venueSlug) {
      return (
        <p className="text-sm text-stone-400 py-10 flex items-center justify-center gap-2" data-testid="pricing-engine-loading">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading calculator configuration…
        </p>
      );
    }
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-8" data-testid="pricing-engine-manager">
        <div className="max-w-lg">
          <div className="flex items-center gap-3 mb-2">
            <Calculator className="w-6 h-6 text-[#1A3626]" />
            <h2 className="font-serif text-xl text-stone-900">Pricing Engine</h2>
          </div>
          <p className="text-sm text-stone-500 mb-6">
            Configure the fully customizable wedding cost calculator for each venue. Every venue can use a different commercial model — full-property packages, per-person pricing, room-based, component-based or any mix.
          </p>
          <label className="text-xs font-medium text-stone-500 mb-1 block">Select a venue to configure</label>
          <select
            value={selectedSlug}
            onChange={e => setSelectedSlug(e.target.value)}
            className="w-full h-11 rounded-md border border-stone-200 bg-white px-3 text-sm"
            data-testid="pricing-venue-select"
          >
            <option value="">— Choose venue —</option>
            {venues.map(v => <option key={v.slug} value={v.slug}>{v.name}</option>)}
          </select>
          {isLoading && <p className="text-xs text-stone-400 mt-3 flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> Loading configuration…</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pricing-engine-manager">
      <div className="bg-white rounded-xl border border-stone-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          {venueSlug ? (
            <p className="text-sm font-medium text-stone-900 flex items-center gap-2" data-testid="pricing-fixed-venue-name">
              <Calculator className="w-4 h-4 text-[#1A3626]" /> Cost Calculator — {venueName}
            </p>
          ) : (
            <select
              value={selectedSlug}
              onChange={e => setSelectedSlug(e.target.value)}
              className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
              data-testid="pricing-venue-select"
            >
              {venues.map(v => <option key={v.slug} value={v.slug}>{v.name}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-600">Calculator live on venue page</span>
          <Switch checked={!!config.enabled} onCheckedChange={v => update('enabled', v)} data-testid="pricing-enabled-toggle" />
        </div>
        <Button onClick={saveConfig} disabled={isSaving || !dirty} className="bg-[#1A3626] hover:bg-[#254834]" data-testid="pricing-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
        </Button>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SUB_TABS.filter(t => !(venueSlug && t.key === 'estimates')).map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${subTab === t.key ? 'bg-[#1A3626] text-white' : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'}`}
            data-testid={`pricing-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-stone-50/60 border border-stone-200 rounded-xl p-5">
        {subTab === 'model' && (
          <div>
            <h3 className="font-medium text-stone-900 mb-1">Selling Model & Modules</h3>
            <p className="text-xs text-stone-500 mb-5">Enable only the modules this venue sells. The customer calculator adapts automatically.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {MODULES.map(m => (
                <div key={m.key} className="bg-white border border-stone-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{m.label}</p>
                    <p className="text-xs text-stone-400">{m.hint}</p>
                  </div>
                  <Switch
                    checked={config.modules?.[m.key] !== false}
                    onCheckedChange={v => updateNested('modules', m.key, v)}
                    data-testid={`pricing-module-${m.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {subTab === 'durations' && (
          <ItemListEditor items={config.durations || []} onChange={v => update('durations', v)} schema={PRICING_SCHEMAS.durations} context={context} testId="pe-list-durations" />
        )}
        {subTab === 'venue_packages' && (
          <ItemListEditor items={config.venue_packages || []} onChange={v => update('venue_packages', v)} schema={PRICING_SCHEMAS.venue_packages} context={context} testId="pe-list-venue-packages" />
        )}
        {subTab === 'per_person_packages' && (
          <ItemListEditor items={config.per_person_packages || []} onChange={v => update('per_person_packages', v)} schema={PRICING_SCHEMAS.per_person_packages} context={context} testId="pe-list-per-person" />
        )}
        {subTab === 'event_spaces' && (
          <ItemListEditor items={config.event_spaces || []} onChange={v => update('event_spaces', v)} schema={PRICING_SCHEMAS.event_spaces} context={context} testId="pe-list-event-spaces" />
        )}
        {subTab === 'room_categories' && (
          <ItemListEditor items={config.room_categories || []} onChange={v => update('room_categories', v)} schema={PRICING_SCHEMAS.room_categories} context={context} testId="pe-list-rooms" />
        )}

        {subTab === 'food' && (
          <div className="space-y-6">
            <div className="max-w-md">
              <label className="text-xs font-medium text-stone-500 mb-1 block">Does this venue provide in-house food?</label>
              <select
                value={config.food?.availability || 'optional'}
                onChange={e => updateNested('food', 'availability', e.target.value)}
                className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
                data-testid="pricing-food-availability"
              >
                {FOOD_AVAILABILITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {config.food?.availability !== 'no' && (
              <ItemListEditor
                items={config.food?.packages || []}
                onChange={v => updateNested('food', 'packages', v)}
                schema={PRICING_SCHEMAS.food_packages}
                context={context}
                testId="pe-list-food"
              />
            )}
          </div>
        )}

        {subTab === 'addons' && (
          <ItemListEditor items={config.addons || []} onChange={v => update('addons', v)} schema={PRICING_SCHEMAS.addons} context={context} testId="pe-list-addons" />
        )}
        {subTab === 'taxes_charges' && (
          <ItemListEditor items={config.taxes_charges || []} onChange={v => update('taxes_charges', v)} schema={PRICING_SCHEMAS.taxes_charges} context={context} testId="pe-list-taxes" />
        )}
        {subTab === 'discounts' && (
          <ItemListEditor items={config.discounts || []} onChange={v => update('discounts', v)} schema={PRICING_SCHEMAS.discounts} context={context} testId="pe-list-discounts" />
        )}

        {subTab === 'seasonal' && (
          <div className="space-y-8">
            <ItemListEditor items={config.seasonal_pricing || []} onChange={v => update('seasonal_pricing', v)} schema={PRICING_SCHEMAS.seasonal_pricing} context={context} testId="pe-list-seasonal" />
            <ItemListEditor items={config.special_dates || []} onChange={v => update('special_dates', v)} schema={PRICING_SCHEMAS.special_dates} context={context} testId="pe-list-special-dates" />
          </div>
        )}

        {subTab === 'rules' && (
          <ItemListEditor items={config.rules || []} onChange={v => update('rules', v)} schema={PRICING_SCHEMAS.rules} context={context} testId="pe-list-rules" />
        )}

        {subTab === 'estimates' && (
          <EstimatesManager adminToken={adminToken} venueSlug={selectedSlug} />
        )}

        {subTab === 'pdf' && (
          <div className="space-y-5 max-w-2xl">
            <h3 className="font-medium text-stone-900">PDF Estimate Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Estimate validity (days)</label>
                <Input
                  type="number"
                  value={config.pdf_settings?.validity_days ?? 15}
                  onChange={e => updateNested('pdf_settings', 'validity_days', Number(e.target.value))}
                  data-testid="pdf-validity-days"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Payment schedule (shown on PDF)</label>
              <Textarea
                rows={3}
                value={config.pdf_settings?.payment_schedule ?? ''}
                placeholder="e.g. 30% to block dates · 50% 30 days before event · 20% on arrival"
                onChange={e => updateNested('pdf_settings', 'payment_schedule', e.target.value)}
                data-testid="pdf-payment-schedule"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Important notes</label>
              <Textarea
                rows={3}
                value={config.pdf_settings?.notes ?? ''}
                onChange={e => updateNested('pdf_settings', 'notes', e.target.value)}
                data-testid="pdf-notes"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Disclaimer</label>
              <Textarea
                rows={3}
                value={config.pdf_settings?.disclaimer ?? ''}
                onChange={e => updateNested('pdf_settings', 'disclaimer', e.target.value)}
                data-testid="pdf-disclaimer"
              />
            </div>
            <div>
              <p className="text-xs font-medium text-stone-500 mb-2">Fields to show in PDF</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PDF_FIELDS.map(([k, l]) => (
                  <div key={k} className="bg-white border border-stone-200 rounded-lg px-3 py-2 flex items-center justify-between">
                    <span className="text-sm text-stone-700">{l}</span>
                    <Switch
                      checked={config.pdf_settings?.show_fields?.[k] !== false}
                      onCheckedChange={v => setConfig(prev => {
                        setDirty(true);
                        return {
                          ...prev,
                          pdf_settings: {
                            ...(prev.pdf_settings || {}),
                            show_fields: { ...(prev.pdf_settings?.show_fields || {}), [k]: v }
                          }
                        };
                      })}
                      data-testid={`pdf-show-field-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {dirty && (
        <div className="sticky bottom-4 flex justify-end">
          <Button onClick={saveConfig} disabled={isSaving} className="bg-[#C6A87C] text-[#0A1710] hover:bg-[#1A3626] hover:text-white shadow-lg" data-testid="pricing-save-floating-btn">
            <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving…' : `Save ${venueName} pricing`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PricingEngineManager;
